import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// pdfjs (v5)
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";

// MUI
import {
    Box,
    Paper,
    Stack,
    Typography,
    TextField,
    Button,
    Backdrop,
    CircularProgress,
    Divider,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LoadingDots from "../../../component/common/LoadingDots";
import ActionButton from "../../../component/common/ActionButton";
import ComboBox from "../../../component/common/ComboBox";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { notify } from "../../../util/toast";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { proposalApi } from "./api/proposalApi";

// ✅ pdfjs worker 설정 (CRA/Webpack5에서 import.meta.url 사용 가능)
try {
    GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
    ).toString();
} catch (e) {
    // fallback: worker 설정 실패해도 getDocument가 동작하는 환경이 있음
    // 필요하면 CDN으로 바꿔도 됨.
    // GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.394/pdf.worker.min.mjs";
}

export default function ProposalUpload() {
    const history = useHistory();
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const id = query.get("id");
    const category = query.get("category") || "";

    const [form, setForm] = useState({
        subCategory: "",
        year: new Date().getFullYear(),
        name: "",
        tags: "",
    });

    const [selectedFile, setSelectedFile] = useState(null); // File
    const [existingPdfUrl, setExistingPdfUrl] = useState(""); // edit 모드에서 기존 PDF url
    const [pdfContents, setPdfContents] = useState(""); // 전체 텍스트
    const [pages, setPages] = useState([]); // [{page, contents}]
    const [isSubmitting, setIsSubmitting] = useState(false);


    const fileInputRef = useRef(null);

    const years = useMemo(() => {
        const now = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => now - i);
    }, []);

    // PDF 텍스트 추출
    const extractTextFromPDF = async (file) => {
        const buf = await file.arrayBuffer();
        const typedArray = new Uint8Array(buf);

        const pdf = await getDocument({ data: typedArray }).promise;

        const tempPages = [];
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const strings = content.items.map((it) => it.str);
            const text = strings.join(" ");
            tempPages.push({ page: i, contents: text });
        }

        const allText = tempPages.map((p) => p.contents).join("\n");
        return { allText, tempPages, numPages: pdf.numPages };
    };

    const handlePickFile = () => {
        if (isSubmitting) return;
        fileInputRef.current?.click();
    };

    const handleFile = async (file) => {
        if (isSubmitting) return;
        if (!file) return;

        // PDF만 허용
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            notify("PDF 파일만 업로드할 수 있습니다.", "error");
            return;
        }

        setSelectedFile(file);
        setExistingPdfUrl(""); // 새 파일 선택하면 기존 URL은 의미 없음

        try {
            notify("PDF 텍스트 추출 중...", "info");
            const { allText, tempPages, numPages } = await extractTextFromPDF(file);
            setPdfContents(allText);
            setPages(tempPages);
            notify(`${file.name} 텍스트 추출 완료 (총 ${numPages}페이지)`, "success");
        } catch (err) {
            console.error(err);
            notify(`PDF 텍스트 추출 실패: ${err?.message || "알 수 없는 오류"}`, "error");
            setPdfContents("");
            setPages([]);
        }
    };

    // Drag & Drop
    const onDragOver = (e) => {
        if (isSubmitting) return;
        e.preventDefault();
        e.stopPropagation();
    };
    const onDrop = (e) => {
        if (isSubmitting) return;
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        handleFile(file);
    };

    // edit 모드: 기존 데이터 로드
    useEffect(() => {
        if (!id) return;

        (async () => {
            try {
                const data = await proposalApi.getById(id);

                setPdfContents(data.contents || "");
                setPages(data.pages || []);
                setExistingPdfUrl(data.path || "");

                setForm({
                    subCategory: data.subCategory || "",
                    year: data.year || new Date().getFullYear(),
                    name: data.name || "",
                    tags: Array.isArray(data.tags) ? data.tags.join(",") : (data.tags || ""),
                });

                notify("기존 데이터를 불러왔습니다.", "info");
            } catch (e) {
                console.error(e);
                notify("기존 데이터 불러오기 실패", "error");
            }
        })();
    }, [id]);

    const setValue = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async () => {
        if (isSubmitting) return;
        // 기본 검증
        if (!form.subCategory) return notify("분류를 선택하세요.", "warning");
        if (!form.year) return notify("년도를 선택하세요.", "warning");
        if (!form.name?.trim()) return notify("행사명을 입력하세요.", "warning");
        // 파일은 신규등록이면 필수, 수정이면 기존 URL이 있으면 optional
        if (!id && !selectedFile) return notify("PDF 파일을 업로드하세요.", "warning");
        if (id && !selectedFile && !existingPdfUrl) return notify("PDF 파일을 업로드하세요.", "warning");

        const tagsArr = form.tags
            ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
            : [];

        const formData = new FormData();
        formData.append("id", id || "");
        formData.append("subCategory", form.subCategory || "");
        formData.append("year", String(form.year || ""));
        formData.append("name", form.name || "");
        formData.append("tags", JSON.stringify(tagsArr));
        formData.append("pages", JSON.stringify(pages || []));
        formData.append("contents", pdfContents || "");

        if (selectedFile) {
            formData.append("file", selectedFile);
        }

        await runApiAction({
            request: () => proposalApi.upload(formData),
            errorMsg: serverMsgOr("업로드 실패"),
            successMsg: id ? "수정되었습니다." : "등록되었습니다.",
            validate: (data) => data?.id && data?.path ? true : "제안서 파일 저장 결과가 비어 있습니다.",
            setLoading: setIsSubmitting,
            onSuccess: () => history.push(`/onandme/proposal?category=${encodeURIComponent(category)}`),
            onError: (error) => console.error(error),
        });
    };

    const fileLabel = selectedFile?.name || (existingPdfUrl ? "기존 PDF가 등록되어 있습니다." : "PDF 파일을 선택하세요");

    return (
        <Box sx={{ maxWidth: 720, mx: "auto", py: 4 }}>
            {/* 로딩 오버레이 */}
            <Backdrop
                open={isSubmitting}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 10,
                    bgcolor: "rgba(255,255,255,0.85)",
                }}
            >
                <LoadingDots label="처리하고 있어요" />
            </Backdrop>

            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                <Stack spacing={2.5}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        제안서 업로드
                    </Typography>

                    <Divider />

                    {/* 분류 */}
                    <ComboBox clearable={false} placeholder="분류" disabled={isSubmitting}
                        options={[
                            { value: "MICE", label: "MICE" },
                            { value: "VIDEO_PLANNING", label: "영상기획" },
                        ]}
                        value={form.subCategory}
                        onChange={(v) => setValue("subCategory", v)} />

                    {/* 년도 */}
                    <ComboBox clearable={false} placeholder="년도" disabled={isSubmitting}
                        options={years.map((y) => ({ value: y, label: String(y) }))}
                        value={form.year}
                        onChange={(v) => setValue("year", v)} />

                    {/* 행사명 */}
                    <TextField
                        label="행사명"
                        value={form.name}
                        onChange={(e) => setValue("name", e.target.value)}
                        fullWidth
                        disabled={isSubmitting}
                    />

                    {/* 태그 */}
                    <TextField
                        label="태그"
                        placeholder="쉼표로 구분 (예: TEL, KERIS, 행사)"
                        value={form.tags}
                        onChange={(e) => setValue("tags", e.target.value)}
                        fullWidth
                        disabled={isSubmitting}
                    />

                    {/* PDF 업로드 영역 */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            PDF 업로드 (1개)
                        </Typography>

                        <Box
                            onDragOver={onDragOver}
                            onDrop={onDrop}
                            onClick={handlePickFile}
                            sx={{
                                cursor: isSubmitting ? "not-allowed" : "pointer",
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 1.5,
                                p: 3,
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                bgcolor: "background.paper",
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        >
                            <PictureAsPdfIcon fontSize="large" />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                    PDF 파일을 드래그하거나 클릭하세요
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    PDF 텍스트 추출 후 업로드에 포함됩니다.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    선택됨: <b>{fileLabel}</b>
                                </Typography>
                            </Box>

                            <ActionButton variant="secondary"
                                startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                                disabled={isSubmitting}
                                label="파일 선택" />

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                style={{ display: "none" }}
                                disabled={isSubmitting}
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                        </Box>

                        {/* 추출 결과 간단 표시 */}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            추출 페이지 수: {pages?.length || 0} / 전체 텍스트 길이: {pdfContents?.length || 0}
                        </Typography>
                    </Box>

                    {/* 등록 버튼 */}
                    <ActionButton
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        label={isSubmitting ? "처리 중" : id ? "수정" : "등록"}
                        sx={{ height: 52 }}
                        startIcon={false}
                    />
                </Stack>
            </Paper>

        </Box>
    );
}
